import React from 'react';
import { FormGroup, Label, Col, Input, FormFeedback, FormText } from 'reactstrap';
import { range } from '~/lib/helper/ArrayHelper';
import { padStart } from '~/lib/helper/StringHelper';
import CardDetailsFormStore from '~/lib/store/CardDetailsFormStore';
import ErrorList from '../ErrorList';
import { observer } from 'mobx-react';

interface CardDetailsFormProps {
    store: CardDetailsFormStore;
}

function formatCardNumber(value: string) {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches?.[0] ?? '';
    const parts = [] as string[];
    for (let i = 0, len = match.length; i < len; i += 4) parts.push(match.substring(i, i + 4));
    return parts.length ? parts.join(' ') : v;
}

@observer
export default class CardDetailsForm extends React.Component<CardDetailsFormProps> {
    render() {
        const { store } = this.props;

        const yearOptions = range(0, 15).map((i) => new Date().getFullYear() + i);

        // we might have an expired card so ensure it is in the list too
        if (store.values.expiry_year) {
            const formYear = parseInt(store.values.expiry_year, 10);
            if (!yearOptions.includes(formYear)) {
                if (yearOptions[0] && formYear < yearOptions[0]) {
                    yearOptions.unshift(formYear);
                } else {
                    yearOptions.push(formYear);
                }
            }
        }

        const monthOptions = range(1, 12).map((i) => padStart(i.toString(), 2, '0'));

        return (
            <>
                <FormGroup row>
                    <Label for="card-type" sm={3}>
                        Card Type
                    </Label>
                    <Col sm={9}>
                        <Input id="card-type" type="select" {...store.inputProps('card_type')}>
                            <option value="">Select...</option>
                            <option value="visa">Visa</option>
                            <option value="mastercard">Mastercard</option>
                            <option value="amex">American Express</option>
                        </Input>
                        <FormFeedback>
                            <ErrorList errors={store.errorsFor('card_type')} />
                        </FormFeedback>
                    </Col>
                </FormGroup>

                <FormGroup row>
                    <Label for="cardholder-name" sm={3}>
                        Cardholder Name
                    </Label>
                    <Col sm={9}>
                        <Input id="cardholder-name" type="text" {...store.inputProps('cardholder')} />
                        <FormFeedback>
                            <ErrorList errors={store.errorsFor('cardholder')} />
                        </FormFeedback>
                    </Col>
                </FormGroup>

                <FormGroup row>
                    <Label for="card-number" sm={3}>
                        Card Number
                    </Label>
                    <Col sm={9}>
                        <Input
                            id="card-number"
                            type="text"
                            {...store.inputProps('number')}
                            onChange={(e) => {
                                const formatted = formatCardNumber(e.currentTarget.value);
                                store.set('number', formatted, 'number');
                            }}
                            maxLength={19}
                        />
                        <FormFeedback>
                            <ErrorList errors={store.errorsFor('number')} />
                        </FormFeedback>
                    </Col>
                </FormGroup>

                <FormGroup row>
                    <Label for="cvv" sm={3}>
                        CVV
                    </Label>
                    <Col sm={9}>
                        <Input
                            id="cvv"
                            type="text"
                            className="maxw-100"
                            {...store.inputProps('cvv')}
                            maxLength={4}
                            onChange={(e) => {
                                const value = e.currentTarget.value.replace(/[^0-9]/g, '');
                                store.set('cvv', value, 'cvv');
                            }}
                        />
                        <FormFeedback>
                            <ErrorList errors={store.errorsFor('cvv')} />
                        </FormFeedback>
                    </Col>
                </FormGroup>

                <FormGroup row>
                    <Label sm={3}>Expiry Date</Label>
                    <Col sm={9}>
                        <div className="d-flex mb-1">
                            <Input
                                id="expiry-month"
                                type="select"
                                className="me-2 maxw-100"
                                aria-label="Expiry month"
                                {...store.inputProps('expiry_month')}
                            >
                                <option value="">Month</option>
                                {monthOptions.map((m) => (
                                    <option key={m} value={m}>
                                        {m}
                                    </option>
                                ))}
                            </Input>
                            <Input
                                id="expiry-year"
                                type="select"
                                className="maxw-120"
                                aria-label="Expiry year"
                                {...store.inputProps('expiry_year')}
                            >
                                <option value="">Year</option>
                                {yearOptions.map((year) => (
                                    <option key={year} value={year}>
                                        {year}
                                    </option>
                                ))}
                            </Input>
                        </div>
                        <FormText>
                            <ErrorList
                                errors={[
                                    ...(store.errorsFor('expiry_month') ?? []),
                                    ...(store.errorsFor('expiry_year') ?? []),
                                ]}
                            />
                        </FormText>
                    </Col>
                </FormGroup>
            </>
        );
    }
}
